import { action, computed } from "@ember/object"
import Component from "@glimmer/component"
import type { EducationRoleSpecific, User } from "@shared/models/user"
import fade from "ember-animated/transitions/fade"
import { isNil, throttle } from "lodash-es"
import styles from "./role-and-title.module.css"

interface Sig {
  Args: {
    updateField: (field: string, value: unknown) => void
    user: User
    next: () => void
  }
}

type EducationRoleSpecificSections = Array<{
  title: string
  /** The educationRole for all users in this section. */
  role: "teacher" | "admin" | "district-admin" | "other"
  /** The educationRoleSpecific options. */
  rolesSpecific: Array<RoleSpecificMeta>
}>

interface RoleSpecificMeta {
  id: EducationRoleSpecific
  text: string
  /** Whether to autofill this.text as the title. */
  autofill?: boolean
}

const SCHOOL_ROLES: EducationRoleSpecificSections = [
  {
    title: "Admin",
    role: "admin",
    rolesSpecific: [
      { id: "SCHOOL_ADMIN_PRINCIPAL", text: "Principal", autofill: true },
      { id: "SCHOOL_ADMIN_ASSISTANT_PRINCIPAL", text: "Assistant Principal", autofill: true },
      { id: "SCHOOL_ADMIN_OTHER", text: "Other Admin" },
    ],
  },
  {
    title: "Teacher",
    role: "teacher",
    rolesSpecific: [
      { id: "TEACHER_GENERAL_EDUCATION", text: "General Education" },
      { id: "TEACHER_SPECIAL_EDUCATION_INCLUSION", text: "Special Education Inclusion" },
      { id: "TEACHER_SPECIAL_EDUCATION_SELF_CONTAINED", text: "Special Education Self-Contained" },
      { id: "TEACHER_ESOL_ELL", text: "ESOL/ELL Inclusion" },
      { id: "TEACHER_CURRICULUM_INSTRUCTIONAL_SUPPORT", text: "Curriculum Instructional Support" },
      { id: "TEACHER_INTERVENTION", text: "Interventionist" },
      { id: "TEACHER_COACH", text: "Instructional Coach", autofill: true },
      { id: "TEACHER_RESOURCE_SPECIALS", text: "Resource/Specials (Art/Music/etc)" },
    ],
  },
  {
    title: "Other",
    role: "other",
    rolesSpecific: [
      { id: "SCHOOL_COUNSELOR_SOCIAL_WORKER", text: "Counselor/Social Worker" },
      { id: "SCHOOL_PARAPROFESSIONAL", text: "Paraprofessional" },
      { id: "SCHOOL_OFFICE_STAFF", text: "Office Staff" },
      { id: "SCHOOL_TECH_COORDINATOR", text: "Tech Coordinator" },
      { id: "SCHOOL_OTHER", text: "Other" },
    ],
  },
]

const DISTRICT_NETWORK_OFFICE_ROLES: EducationRoleSpecificSections = [
  {
    title: "",
    role: "district-admin",
    rolesSpecific: [
      { id: "DISTRICT_ADMIN_SUPERINTENDENT_CEO", text: "Superintendent/CEO", autofill: true },
      {
        id: "DISTRICT_ADMIN_CHIEF_ACADEMIC_OFFICER",
        text: "Chief Academic Officer",
        autofill: true,
      },
      {
        id: "DISTRICT_ADMIN_DIRECTOR_OF_CURRICULUM",
        text: "Director of Curriculum",
        autofill: true,
      },
      { id: "DISTRICT_ADMIN_HEAD_OF_SUBJECT", text: "Head of a Subject Area" },
      { id: "DISTRICT_ADMIN_IT_DIRECTOR", text: "IT Director", autofill: true },
      { id: "DISTRICT_ADMIN_OTHER", text: "Other" },
    ],
  },
]
export default class RoleAndTitleComponent extends Component<Sig> {
  readonly styles = styles
  fadeTransition = fade

  get canShowRole() {
    return this.args.user.attributes.workplace !== "OTHER"
  }

  @computed("args.user.attributes.educationRoleSpecific", "args.user.attributes.workplace")
  get canShowTitle(): boolean {
    return (
      this.args.user.attributes.workplace === "OTHER" ||
      !isNil(this.args.user.attributes.educationRoleSpecific)
    )
  }

  @computed("args.user.attributes.title", "canShowTitle")
  get canShowNext() {
    // console.log("this.args.user.attributes.title", this.args.user.attributes.title)
    return this.canShowTitle && !isNil(this.args.user.attributes.title)
  }

  @action updateRoles(
    role: "teacher" | "admin" | "district-admin" | "other",
    roleSpecific: RoleSpecificMeta
  ): void {
    this.args.updateField("educationRole", role)
    this.args.updateField("educationRoleSpecific", roleSpecific.id)
    if (roleSpecific.autofill) {
      this.args.updateField("title", roleSpecific.text)
    }
  }

  // We throttle so we can show the next as soon as they start typing
  updateTitle = throttle(
    (val) => {
      console.log("trailing?")
      this.args.updateField("title", val)
    },
    500,
    { trailing: true, leading: false }
  )

  get sections(): EducationRoleSpecificSections {
    if (!this.args.user) return []

    const workplace = this.args.user.attributes.workplace
    switch (workplace) {
      case "SCHOOL":
        return SCHOOL_ROLES
      case "DISTRICT_NETWORK_OFFICE":
        return DISTRICT_NETWORK_OFFICE_ROLES
      default: {
        return []
      }
    }
  }
}
